// One way to read, show and write an agent's model (Hosted Model Serving
// Part II, phase M — M4-2).
//
// An agent either follows the workspace default (it changes when an admin
// re-ranks models) or is pinned by a person to one model:
//
//   flow_config.llm.mode = "default"                       → "Workspace default (currently X)"
//   flow_config.llm.mode = "pinned" + provider/model(/endpoint_id) → "Pinned: provider / model"
//
// Every model picker writes ONLY flow_config.llm.mode (+ the pinned target);
// the backend's one writer keeps the provider/model columns in step. Agents
// saved before phase M have no mode yet: their provider/model columns are
// what text chat used, so they display as pinned.

export const WORKSPACE_DEFAULT = "__workspace_default__";

const EMPTY = { provider: null, model: null, endpoint_id: null };

/** The agent's model setting: {mode: "default"|"pinned", provider, model, endpoint_id}. */
export function modelSettingOf(agent) {
  const llm = agent?.flow_config?.llm;
  if (llm && typeof llm === "object" && "mode" in llm) {
    if (llm.mode === "pinned" && llm.provider && llm.model) {
      return { mode: "pinned", provider: llm.provider, model: llm.model, endpoint_id: llm.endpoint_id || null };
    }
    return { mode: "default", ...EMPTY };
  }
  if (agent?.provider && agent?.model) {
    // Saved before phase M: the columns are what text chat used.
    const endpoint = llm && typeof llm === "object" ? llm.endpoint_id || null : null;
    return { mode: "pinned", provider: agent.provider, model: agent.model, endpoint_id: endpoint };
  }
  return { mode: "default", ...EMPTY };
}

function targetText({ provider, model, endpoint_id }) {
  if (String(provider || "").startsWith("local_")) {
    return endpoint_id ? `${model} @ ${endpoint_id}` : model;
  }
  return `${provider} / ${model}`;
}

/** "Workspace default (currently X)" | "Workspace default" | "Pinned: provider / model". */
export function modelLabel(setting, workspaceDefault) {
  if (setting?.mode === "pinned") return `Pinned: ${targetText(setting)}`;
  if (workspaceDefault?.model) return `Workspace default (currently ${targetText(workspaceDefault)})`;
  return "Workspace default";
}

/** Short form for tight spaces (tiles, nav): the model, or "Workspace default". */
export function modelShortLabel(setting) {
  return setting?.mode === "pinned" ? setting.model : "Workspace default";
}

export function encodeChoice({ provider, model, endpoint_id }) {
  return [provider, model, endpoint_id || ""].join("|");
}

/** null = Workspace default; otherwise {provider, model, endpoint_id}. */
export function decodeChoice(value) {
  if (!value || value === WORKSPACE_DEFAULT) return null;
  const [provider, model, endpoint_id] = String(value).split("|");
  if (!provider || !model) return null;
  return { provider, model, endpoint_id: endpoint_id || null };
}

/** The select value for a setting. */
export function choiceValue(setting) {
  return setting?.mode === "pinned" ? encodeChoice(setting) : WORKSPACE_DEFAULT;
}

/**
 * Picker options from GET /api/llm/models data ({provider: [{id, kind, endpoint_id?}]}):
 * "Workspace default (currently X)" first, then every chat model. A pinned
 * value that is no longer offered stays selectable so it is never silently lost.
 */
export function modelChoiceOptions(models, workspaceDefault, current) {
  const options = [{ value: WORKSPACE_DEFAULT, label: modelLabel({ mode: "default" }, workspaceDefault) }];
  for (const [provider, list] of Object.entries(models || {})) {
    for (const m of list || []) {
      if (m?.kind && m.kind !== "chat") continue;
      const model = m?.id || m?.name;
      if (!model) continue;
      const target = { provider, model, endpoint_id: m.endpoint_id || null };
      options.push({ value: encodeChoice(target), label: targetText(target) });
    }
  }
  if (current?.mode === "pinned" && !options.some((o) => o.value === encodeChoice(current))) {
    options.push({ value: encodeChoice(current), label: `${targetText(current)} (not currently offered)` });
  }
  return options;
}

/** The partial patch a picker saves: only flow_config.llm.mode (+ pinned target). */
export function modelPatch(choice) {
  if (!choice) return { flow_config: { llm: { mode: "default" } } };
  const llm = { mode: "pinned", provider: choice.provider, model: choice.model };
  if (choice.endpoint_id) llm.endpoint_id = choice.endpoint_id;
  return { flow_config: { llm } };
}
